import Box from '@material-ui/core/Box';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import React, { Fragment } from 'react';
import { useQuery } from 'react-query';
import { SpotlightPermission } from 'types/enums';
import Header from '../components/Header';
import { usePermissions } from '../hooks/usePermissions';
import { fetchDashboardUrls } from 'api';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    height: '100%',
    minHeight: '100vh',
  },
  container: {
    padding: theme.spacing(4),
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 500,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    border: '1px solid #E0E0E0',
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: theme.palette.primary.main,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
  },
  listItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
  },
  listItemText: {
    fontSize: '1rem',
    fontWeight: 400,
    color: '#44444F',
  },
  icon: {
    color: theme.palette.primary.main,
  },
  accessDeniedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 200px)',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  accessDeniedTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  accessDeniedMessage: {
    color: theme.palette.text.secondary,
    maxWidth: '500px',
  },
}));

const Dashboards: React.FC = () => {
  const { isPermitted } = usePermissions();
  const classes = useStyles();

  const canAccessDashboards = isPermitted([SpotlightPermission.VIEW_MONITORING_DASHBOARDS], 'any');

  const { isLoading, data, error } = useQuery(['dashboardUrls'], fetchDashboardUrls, {
    enabled: !!canAccessDashboards,
    retry: false,
  });

  const dashboards = data?.details || [];

  const handleOpenDashboard = (url: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!canAccessDashboards) {
    return (
      <Fragment>
        <Header activeTab="dashboards" />

        <Box className={classes.accessDeniedContainer}>
          <Typography variant="h5" color="error" className={classes.accessDeniedTitle}>
            Access Denied!
          </Typography>
          <Typography variant="body1" className={classes.accessDeniedMessage}>
            This page is only accessible to Walmart related users.
          </Typography>
          <Typography variant="body1" className={classes.accessDeniedMessage}>
            Please contact your administrator to request access.
          </Typography>
        </Box>
      </Fragment>
    );
  }

  if (isLoading) {
    return (
      <Fragment>
        <Header activeTab="dashboards" />
        <Box className={classes.accessDeniedContainer}>
          <Typography variant="h6" className={classes.accessDeniedMessage}>
            Loading dashboards...
          </Typography>
        </Box>
      </Fragment>
    );
  }

  if (error) {
    const errorMessage = (error as any)?.response?.data?.message || 'Failed to load dashboard URLs';
    return (
      <Fragment>
        <Header activeTab="dashboards" />
        <Box className={classes.accessDeniedContainer}>
          <Typography variant="h5" color="error" className={classes.accessDeniedTitle}>
            Error Loading Dashboards
          </Typography>
          <Typography variant="body1" className={classes.accessDeniedMessage}>
            {errorMessage}
          </Typography>
        </Box>
      </Fragment>
    );
  }

  if (!dashboards || dashboards.length === 0) {
    return (
      <Fragment>
        <Header activeTab="dashboards" />
        <Box className={classes.accessDeniedContainer}>
          <Typography variant="h5" color="error" className={classes.accessDeniedTitle}>
            No Dashboard URLs Available
          </Typography>
          <Typography variant="body1" className={classes.accessDeniedMessage}>
            Dashboard URLs could not be loaded. Please try again later.
          </Typography>
        </Box>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Header activeTab="dashboards" />

      <div className={classes.root}>
        <Box className={classes.container}>
          <Typography variant="h4" className={classes.title}>
            Dashboards
          </Typography>
          <Box className={classes.listContainer}>
            {dashboards.map((dashboard: { header: string; url: string }, index: number) => (
              <Box
                key={index}
                className={classes.listItem}
                onClick={() => handleOpenDashboard(dashboard.url)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpenDashboard(dashboard.url);
                  }
                }}
                aria-label={`Open ${dashboard.header} dashboard`}
              >
                <Box className={classes.listItemContent}>
                  <Typography className={classes.listItemText}>{dashboard.header}</Typography>
                </Box>
                <OpenInNewIcon className={classes.icon} />
              </Box>
            ))}
          </Box>
        </Box>
      </div>
    </Fragment>
  );
};

export default Dashboards;
